// Sorting and narrowing a device list in the browser (#1259, #834), on the
// whole list the page holds.
//
// **Only over a list read to its end.** Every caller reads its devices with
// `useDrained`, so what is reordered or narrowed here is the site's (or the
// customer's) whole list and not a page of it — #251's disposition for client
// devices, which is complete for the site in one read. Narrowing a first page
// would be the filter that lies #834 warned about: "nothing offline" said of
// the first 200.
//
// **Nothing here asks the server anything**, and nothing is carried in the
// address or remembered, as on Client devices. No list endpoint takes a sort
// parameter and the cursor is the serial, so an order is the browser's.
import { type ReactNode } from "react";
import type { Device } from "./commands";
import { AscendingIcon, Glyph, OpenIcon } from "../../icons";

/** One column's order: ascending, then descending, then the API's own. */
export type Sort<K extends string> = { key: K; dir: "asc" | "desc" };

/** The next sort a click on `key` gives, from `current`: a new column starts
 * ascending, the same column turns descending, and a third click returns to
 * the API's order — as `Clients.tsx` toggles. */
export function nextSort<K extends string>(current: Sort<K> | undefined, key: K): Sort<K> | undefined {
  if (current?.key !== key) return { key, dir: "asc" };
  return current.dir === "asc" ? { key, dir: "desc" } : undefined;
}

/** A value a row sorts on. `undefined` is an absence — a column whose read is
 * out, a figure the report did not carry — and sorts **last in both
 * directions**: an absence is not the smallest value, and putting it first
 * when the oldest contact is asked for would put a row that says nothing above
 * the one that answers (`sortStations`, #251). */
export type SortValue = string | number | undefined;

/** `rows` in `sort`'s order, by the value `by[key]` gives each row. Text
 * compares as a person reads it — `AP-2` before `AP-10`, case aside. Ties
 * keep the API's order, which is the serial's. Without a sort the rows are
 * returned as given. */
export function sortRows<R, K extends string>(rows: R[], sort: Sort<K> | undefined, by: Record<K, (r: R) => SortValue>): R[] {
  if (!sort) return rows;
  const sign = sort.dir === "asc" ? 1 : -1;
  const of = by[sort.key];
  const keyed = rows.map((r, i) => ({ r, i, v: of(r) }));
  keyed.sort((a, b) => {
    if (a.v === undefined || b.v === undefined) return a.v === b.v ? a.i - b.i : a.v === undefined ? 1 : -1;
    const c = typeof a.v === "number" && typeof b.v === "number"
      ? a.v - b.v
      : String(a.v).localeCompare(String(b.v), undefined, { numeric: true, sensitivity: "base" });
    return c !== 0 ? sign * c : a.i - b.i;
  });
  return keyed.map((k) => k.r);
}

/** A sortable column head: `.th-sort` inside the `th`, with `aria-sort` on the
 * `th`, as Client devices draws one (#251). */
export function SortHeader<K extends string>({ k, sort, onSort, title, className, children }: {
  k: K; sort?: Sort<K>; onSort(key: K): void; title?: string; className?: string; children: ReactNode;
}) {
  const active = sort?.key === k;
  return (
    <th scope="col" className={className} title={title} aria-sort={active ? (sort.dir === "asc" ? "ascending" : "descending") : "none"}>
      <button type="button" className="th-sort" onClick={() => onSort(k)}>
        {children}
        {active && <Glyph icon={sort.dir === "asc" ? AscendingIcon : OpenIcon} size={12} className="sort-mark" />}
      </button>
    </th>
  );
}

/** A serial as typed, with its separators gone: `5c:17:83` finds
 * `5c17834a9ac0`, the way a MAC matches on Client devices, because a serial is
 * copied from a label, a log or a controller in whichever spelling it had. */
export const bareSerial = (s: string) => s.toLowerCase().replace(/[:.\-\s]/g, "");

/** Whether a device matches what was typed: its name, its serial with or
 * without separators, its model — the cell's string and the catalogue model it
 * resolved to — and its firmware. An empty box matches everything. */
export function matchesText(d: Pick<Device, "name" | "serial" | "manufacturer" | "firmware" | "catalogue_model">, typed: string): boolean {
  const needle = typed.trim().toLowerCase();
  if (!needle) return true;
  const bare = bareSerial(needle);
  return (bare !== "" && d.serial.toLowerCase().includes(bare))
    || d.name.toLowerCase().includes(needle)
    || (d.manufacturer ?? "").toLowerCase().includes(needle)
    || (d.catalogue_model?.model ?? "").toLowerCase().includes(needle)
    || (d.firmware ?? "").toLowerCase().includes(needle);
}

/** `online_state`'s three answers, as the server gives them (ADR-0219). The
 * picker narrows on the field and derives nothing: whether a device is online
 * is `OnlineAt`'s answer, read once on the server (§9.4), and a second opinion
 * here would be the second definition that rule forbids. */
// Restates ADR-0219 decisions 1 and 3 — `unknown` is a state of its own and not a kind of
// offline, so the picker offers it apart rather than folding it into "not online".
export const LIVENESS = ["online", "offline", "unknown"] as const;
export type Liveness = typeof LIVENESS[number];

/** The `K of N` a narrowed list says, against the whole list it narrows. */
export function shownOf(shown: number, total: number, narrowed: boolean): string | undefined {
  return narrowed ? `${shown} of ${total} shown` : undefined;
}
